import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ActionButton,
  Callout,
  CommandBar,
  DirectionalHint,
  FontIcon,
  ICommandBarItemProps,
  IconButton,
  Persona,
  PersonaSize,
  Separator,
  Stack,
  Text,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { Preferences } from 'Preferences';
import clsx from 'clsx';
import { Badge } from 'common/components/Badge';
import { setPanelWidth } from 'common/components/DraggablePanel';
import { NOTIFICATION_POLL_INTERVAL } from 'common/constants';
import { useCommonStyles } from 'common/styles';
import { getHoverColor } from 'common/utils/color';
import { config } from 'config';
import { MessagingView } from 'messaging/view';
import { NotificationView } from 'notification/view';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  expandedNav,
  highlightTextMode,
  shouldPollNotificationQuery,
} from 'utility/cache/ui';
import Logo from '../assets/svgs/logo.svg?react';
import CollapsedLogo from '../assets/svgs/logo_collapsed.svg?react';
import COUNTS_QUERY from './GetNotificationMessageCount.graphql';
import { GlobalSearch } from './GlobalSearch';
import UNREAD_QUERY from './UserMessageUnreadCountReminderUpdate.graphql';
import { GetNotificationMessageCount } from './__generated__/GetNotificationMessageCount';
import {
  UserMessageUnreadCountReminderUpdate,
  UserMessageUnreadCountReminderUpdateVariables,
} from './__generated__/UserMessageUnreadCountReminderUpdate';
import { useStyles } from './index.styles';

export const TopBar: React.FC = () => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const theme = useTheme();
  const highlightTextState = useReactiveVar(highlightTextMode);
  const shouldPoll = useReactiveVar(shouldPollNotificationQuery);
  const [isMessagesOpen, setMessagesOpen] = useState(false);
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);

  const renderRef = useRef<boolean>(true);
  const messageRef = React.useRef<HTMLDivElement | null>(null);
  const ringerRef = useRef<HTMLDivElement>(null);

  const expanded = useReactiveVar(expandedNav);
  const [preferenceVisible, setPreferenceVisible] = useState<boolean>(false);
  const [checkedNotifications, setCheckedNotifications] =
    useState<boolean>(true);
  const [checkedMessages, setCheckedMessages] = useState<boolean>(true);
  const [isNotificationsHovered, setIsNotificationsHovered] = useState(false);
  const [isMessagesHovered, setIsMessagesHovered] = React.useState(false);
  const { user, logout } = useAuth0();
  const preferenceCalloutId = useId('callout-preferenceCalloutId');
  const { data } = useQuery<GetNotificationMessageCount>(COUNTS_QUERY, {
    pollInterval: shouldPoll ? NOTIFICATION_POLL_INTERVAL : 0,
  });

  const [fetchData, { data: unreadCountData }] = useMutation<
    UserMessageUnreadCountReminderUpdate,
    UserMessageUnreadCountReminderUpdateVariables
  >(UNREAD_QUERY);

  const newMessageBadgeVisible =
    renderRef.current === true
      ? !!unreadCountData?.userMessageUnreadCountReminderUpdate
        ?._NewUserMessageCounts
      : !!data?.userMessageUnreadCounts;

  const items: ICommandBarItemProps[] = [
    {
      key: 'notifications',
      className: styles.iconButton,
      onRender: () => (
        <TooltipHost content="System Notifications">
          <Stack
            horizontal
            verticalAlign="center"
            className={styles.commandButton}
            style={{
              marginLeft:
                !checkedNotifications && ringerRef.current ? 170 : undefined,
            }}
            tokens={{ childrenGap: 12 }}
            onClick={() => {
              setPanelWidth(undefined);
              setNotificationsOpen(true);
            }}
          >
            <div ref={ringerRef} className={styles.iconWrapper}>
              <Badge visible={!!data?.userNotificationUnreadCounts}>
                <FontIcon iconName="Ringer" className={styles.icon} />
              </Badge>
            </div>
            {!checkedNotifications && ringerRef.current && (
              <Callout
                target={ringerRef.current}
                directionalHint={DirectionalHint.leftCenter}
                isBeakVisible
                beakWidth={8}
                gapSpace={8}
                styles={{
                  root: clsx(
                    styles.calloutRoot,
                    isNotificationsHovered && styles.calloutHovered
                  ),
                  beak: clsx(
                    styles.calloutBeak,
                    isNotificationsHovered && styles.calloutBeakHovered
                  ),
                  beakCurtain: clsx(
                    styles.calloutBeak,
                    isNotificationsHovered && styles.calloutBeakHovered
                  ),
                  calloutMain: clsx(
                    styles.calloutMain,
                    isNotificationsHovered && styles.calloutMainHovered
                  ),
                }}
                onDismiss={() => setCheckedNotifications(true)}
                preventDismissOnLostFocus
                preventDismissOnScroll
              >
                <Stack
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                  onMouseEnter={() => setIsNotificationsHovered(true)}
                  onMouseLeave={() => setIsNotificationsHovered(false)}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setPanelWidth(undefined);
                    setNotificationsOpen(true);
                  }}
                >
                  <IconButton
                    className={styles.closeButton}
                    iconProps={{
                      iconName: 'Cancel',
                      className: clsx(
                        styles.closeIcon,
                        isNotificationsHovered && styles.closeIconHovered
                      ),
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCheckedNotifications(true);
                    }}
                  />

                  <Text
                    variant="mediumPlus"
                    className={clsx(
                      styles.calloutText,
                      isNotificationsHovered && styles.calloutTextHovered
                    )}
                  >
                    New Notifications
                  </Text>
                </Stack>
              </Callout>
            )}
          </Stack>
        </TooltipHost>
      ),
    },
    {
      key: 'messages',
      className: styles.iconButton,
      onRender: () => {
        return (
          <TooltipHost content="User Messages">
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 12 }}
              className={styles.commandButton}
              style={{
                marginRight:
                  !checkedMessages && messageRef.current ? 150 : undefined,
              }}
              onClick={() => {
                setPanelWidth(undefined);
                setMessagesOpen(true);
              }}
            >
              <div
                ref={messageRef}
                className={styles.iconWrapper}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Badge visible={newMessageBadgeVisible}>
                  <FontIcon iconName="Message" className={styles.icon} />
                </Badge>
              </div>

              {!checkedMessages && messageRef.current && (
                <Callout
                  target={messageRef.current}
                  directionalHint={DirectionalHint.rightCenter}
                  directionalHintFixed
                  isBeakVisible
                  beakWidth={8}
                  gapSpace={8}
                  styles={{
                    root: clsx(
                      styles.calloutRoot,
                      styles.calloutWidthMessage,
                      isMessagesHovered && styles.calloutHovered
                    ),
                    beak: clsx(
                      styles.calloutBeak,
                      isMessagesHovered && styles.calloutBeakHovered
                    ),
                    beakCurtain: clsx(
                      styles.calloutBeak,
                      isMessagesHovered && styles.calloutBeakHovered
                    ),
                    calloutMain: clsx(
                      styles.calloutMain,
                      isMessagesHovered && styles.calloutMainHovered
                    ),
                  }}
                  style={{ width: 135 }}
                  onDismiss={() => setCheckedMessages(true)}
                  preventDismissOnLostFocus
                  preventDismissOnScroll
                >
                  <Stack
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: 8 }}
                    onMouseEnter={() => setIsMessagesHovered(true)}
                    onMouseLeave={() => setIsMessagesHovered(false)}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setPanelWidth(undefined);
                      setMessagesOpen(true);
                    }}
                  >
                    <Text
                      variant="mediumPlus"
                      className={clsx(
                        styles.calloutText,
                        isMessagesHovered && styles.calloutTextHovered
                      )}
                    >
                      New Messages
                    </Text>
                    <IconButton
                      className={styles.closeButton}
                      iconProps={{
                        iconName: 'Cancel',
                        className: clsx(
                          styles.closeIcon,
                          isMessagesHovered && styles.closeIconHovered
                        ),
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCheckedMessages(true);
                      }}
                    />
                  </Stack>
                </Callout>
              )}
            </Stack>
          </TooltipHost>
        );
      },
    },

    {
      key: 'user',
      className: styles.iconButton,
      // menuIconProps isn't defined by the fluent-ui type definition, it's a bug in the library
      // https://github.com/microsoft/fluentui/issues/10569
      menuIconProps: { style: { display: 'none' } },
      onRenderIcon: () => (
        <TooltipHost content="Preferences / Sign out">
          <Persona
            hidePersonaDetails
            text={user?.name}
            size={PersonaSize.size32}
          />
        </TooltipHost>
      ),
      subMenuProps: {
        items: [
          {
            key: 'Preferences',
            text: 'Preferences',
            iconProps: { iconName: 'Settings' },
            onClick: () => setPreferenceVisible(true),
          },
          {
            key: 'logout',
            text: 'Sign out',
            iconProps: { iconName: 'leave' },
            onClick: () =>
              logout({
                clientId: config!.auth0.clientId,
                logoutParams: {
                  returnTo: window.location.origin,
                },
              }),
          },
        ],
      },
    },
  ];

  const fetchCountDataCallback = useCallback(() => {
    fetchData({
      variables: {
        input: { updateAllReminders: true },
      },
    });
  }, [fetchData]);

  useEffect(() => {
    fetchCountDataCallback();
    renderRef.current = false;
  }, [fetchCountDataCallback]);

  useEffect(() => {
    const hasMessages = !!data?.userMessageUnreadCounts;
    const hasNotifications = !!data?.userNotificationUnreadCounts;
    shouldPollNotificationQuery(!(hasMessages && hasNotifications));
    setCheckedMessages(!hasMessages);
    setCheckedNotifications(!hasNotifications);
  }, [data]);

  return (
    <>
      {isNotificationsOpen && (
        <NotificationView
          isOpened={isNotificationsOpen}
          closePanel={() => {
            setPanelWidth(undefined);
            setNotificationsOpen(false);
            shouldPollNotificationQuery(true);
            setCheckedNotifications(true);
          }}
        />
      )}
      {isMessagesOpen && (
        <MessagingView
          isOpenPanel={isMessagesOpen}
          onPanelDismiss={() => {
            setPanelWidth(undefined);
            setMessagesOpen(false);
            shouldPollNotificationQuery(true);
            setCheckedMessages(true);
          }}
        />
      )}
      {preferenceVisible && (
        <Preferences
          isOpen={preferenceVisible}
          calloutId={preferenceCalloutId}
          onDismiss={() => setPreferenceVisible(false)}
        />
      )}
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        className={styles.root}
      >
        <Stack horizontal className={styles.logoContainer}>
          <Stack
            horizontal
            verticalAlign="end"
            className={
              expanded
                ? styles.logoContainerExpanded
                : styles.logoContainerCollapsed
            }
          >
            <Link className={styles.logo} to="/">
              {expanded ? (
                <>
                  <Logo />
                  <Text
                    variant="mediumPlus"
                    className={clsx(styles.logoText, commonStyles.semibold)}
                  >
                    Expenses
                  </Text>
                </>
              ) : (
                <CollapsedLogo />
              )}
            </Link>
          </Stack>
        </Stack>
        <Stack horizontal verticalAlign="center">
          <GlobalSearch />

          <TooltipHost content="Highlight search terms">
            <ActionButton
              iconProps={{
                iconName: highlightTextState
                  ? 'FabricTextHighlightComposite'
                  : 'FabricTextHighlight',
              }}
              styles={{
                iconPressed: {
                  color: highlightTextState
                    ? getHoverColor(theme.palette.orangeLighter, theme)
                    : getHoverColor(theme.palette.neutralTertiary, theme),
                },
                icon: {
                  fontSize: 22,
                  color: highlightTextState
                    ? theme.palette.orangeLighter
                    : theme.palette.neutralTertiary,
                },
                iconHovered: {
                  color: highlightTextState
                    ? getHoverColor(theme.palette.orangeLighter, theme)
                    : getHoverColor(theme.palette.neutralTertiary, theme),
                },
              }}
              onClick={() => highlightTextMode(!highlightTextState)}
            />
          </TooltipHost>
          <Separator vertical />
          <CommandBar
            id={preferenceCalloutId}
            items={items}
            onReduceData={() => undefined}
            styles={{
              root: { height: 48, padding: 0 },
              primarySet: {
                display: 'flex',
                alignItems: 'center',
                gap: 0,
              },
            }}
          />
        </Stack>
      </Stack>
    </>
  );
};
